import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Plus, Pencil, Trash2, CakeSlice } from "lucide-react";
import type { ArticleRecipeWithBase, BaseRecipeWithItems } from "@shared/schema";

const articleRecipeFormSchema = z.object({
  name: z.string().min(1, "Namn krävs"),
  baseRecipeId: z.string().min(1, "Välj ett basrecept"),
  weight: z.coerce.number().min(1, "Artikelvikt krävs"),
});

type ArticleRecipeFormData = z.infer<typeof articleRecipeFormSchema>;

export default function AdminArticleRecipes() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<ArticleRecipeWithBase | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: recipes, isLoading } = useQuery<ArticleRecipeWithBase[]>({
    queryKey: ["/api/admin/article-recipes"],
  });

  const { data: baseRecipes } = useQuery<BaseRecipeWithItems[]>({
    queryKey: ["/api/admin/base-recipes"],
  });

  const form = useForm<ArticleRecipeFormData>({
    resolver: zodResolver(articleRecipeFormSchema),
    defaultValues: { name: "", baseRecipeId: "", weight: 0 },
  });

  const createMutation = useMutation({
    mutationFn: (data: ArticleRecipeFormData) =>
      apiRequest("POST", "/api/admin/article-recipes", data),
    onSuccess: () => {
      toast({ title: "Artikelrecept skapat!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/article-recipes"] });
      setDialogOpen(false);
      form.reset();
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte skapa artikelrecept.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ArticleRecipeFormData & { id: string }) =>
      apiRequest("PATCH", `/api/admin/article-recipes/${data.id}`, data),
    onSuccess: () => {
      toast({ title: "Artikelrecept uppdaterat!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/article-recipes"] });
      setDialogOpen(false);
      setEditingRecipe(null);
      form.reset();
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte uppdatera artikelrecept.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/article-recipes/${id}`),
    onSuccess: () => {
      toast({ title: "Artikelrecept raderat!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/article-recipes"] });
      setDeletingId(null);
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte radera artikelrecept.", variant: "destructive" });
    },
  });

  const openCreate = () => {
    setEditingRecipe(null);
    form.reset({ name: "", baseRecipeId: "", weight: 0 });
    setDialogOpen(true);
  };

  const openEdit = (recipe: ArticleRecipeWithBase) => {
    setEditingRecipe(recipe);
    form.reset({
      name: recipe.name,
      baseRecipeId: recipe.baseRecipeId,
      weight: parseFloat(recipe.weight),
    });
    setDialogOpen(true);
  };

  const onSubmit = (data: ArticleRecipeFormData) => {
    if (editingRecipe) {
      updateMutation.mutate({ ...data, id: editingRecipe.id });
    } else {
      createMutation.mutate(data);
    }
  };

  const watchBaseRecipeId = form.watch("baseRecipeId");
  const watchArticleWeight = form.watch("weight");
  const selectedBase = baseRecipes?.find((r) => r.id === watchBaseRecipeId);
  const baseTotalWeight = selectedBase?.items.reduce((sum, item) => sum + parseFloat(item.weight), 0) || 0;
  const baseTotalPrice = selectedBase?.totalPrice || 0;
  const quantityFromBase = watchArticleWeight > 0 && baseTotalWeight > 0 ? baseTotalWeight / watchArticleWeight : 0;
  const pricePerPiece = quantityFromBase > 0 ? baseTotalPrice / quantityFromBase : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Artikelrecept</h1>
        <p className="text-muted-foreground">Koppla basrecept till artiklar med automatisk priskalkyl</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>
            <CardTitle>Artikelrecept</CardTitle>
            <CardDescription>Alla artikelrecept med priskalkyler</CardDescription>
          </div>
          <Button onClick={openCreate} data-testid="button-add-article-recipe">
            <Plus className="h-4 w-4 mr-2" />
            Nytt artikelrecept
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : recipes && recipes.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Namn</TableHead>
                    <TableHead>Basrecept</TableHead>
                    <TableHead>Artikelvikt</TableHead>
                    <TableHead>Antal/bas</TableHead>
                    <TableHead>Pris/st</TableHead>
                    <TableHead className="text-right">Åtgärd</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recipes.map((recipe) => (
                    <TableRow key={recipe.id}>
                      <TableCell className="font-medium">{recipe.name}</TableCell>
                      <TableCell>{recipe.baseRecipe?.name || "-"}</TableCell>
                      <TableCell>{recipe.weight}g</TableCell>
                      <TableCell>{recipe.quantityFromBase?.toFixed(1) || "-"}</TableCell>
                      <TableCell>{recipe.pricePerPiece?.toFixed(2) || "-"} kr</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 flex-wrap">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(recipe)} data-testid={`button-edit-article-${recipe.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <AlertDialog open={deletingId === recipe.id} onOpenChange={(open) => !open && setDeletingId(null)}>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeletingId(recipe.id)} data-testid={`button-delete-article-${recipe.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Radera artikelrecept?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Detta kommer att permanent radera {recipe.name}.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteMutation.mutate(recipe.id)} className="bg-destructive text-destructive-foreground">
                                  Radera
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <CakeSlice className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Inga artikelrecept registrerade</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRecipe ? "Redigera artikelrecept" : "Nytt artikelrecept"}</DialogTitle>
            <DialogDescription>
              {editingRecipe ? "Uppdatera artikelrecept" : "Koppla en artikel till ett basrecept"}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Artikelnamn</FormLabel>
                    <FormControl>
                      <Input {...field} data-testid="input-article-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="baseRecipeId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Basrecept</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid="select-base-recipe">
                          <SelectValue placeholder="Välj basrecept" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {baseRecipes?.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name} ({r.totalPrice?.toFixed(2)} kr)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="weight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Artikelvikt (gram)</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" min={1} data-testid="input-article-weight" />
                    </FormControl>
                    <FormDescription>Vikten per artikel i gram</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {selectedBase && watchArticleWeight > 0 && (
                <div className="rounded-lg border p-3 space-y-2 bg-muted/30">
                  <p className="text-sm font-medium">Kalkyl</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-muted-foreground">Basvikt totalt:</span>
                    <span>{baseTotalWeight}g</span>
                    <span className="text-muted-foreground">Baskostnad:</span>
                    <span>{baseTotalPrice.toFixed(2)} kr</span>
                    <span className="text-muted-foreground">Antal per bas:</span>
                    <span>{quantityFromBase.toFixed(1)} st</span>
                    <span className="text-muted-foreground font-medium">Pris per styck:</span>
                    <span className="font-medium">{pricePerPiece.toFixed(2)} kr</span>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Avbryt
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending} data-testid="button-save-article-recipe">
                  {(createMutation.isPending || updateMutation.isPending) ? "Sparar..." : "Spara"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
